import { Button, Card } from '@mui/material';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import type { InvoiceMonthlyDetailFeeType, InvoiceMonthlyDetailParameters, SubscriberType } from '@parkchirp/reporting';
import { invoiceMonthlyDetailDefaultFeeTypes } from '@parkchirp/reporting';
import { useCallback, useMemo, useState } from 'react';
import { dayjs } from '../../utils/dayjs';
import { AccountFilter, type AccountFilterValue } from './AccountFilter';
import { FacilityHierarchyFilter, type FacilityHierarchyFilterValue } from './FacilityHierarchyFilter';
import { FeeTypesFilter } from './FeeTypesFilter';
import { applyFacilityHierarchyToParameters } from './facilityHierarchyParameters';
import {
	InvoiceMonthlyDetailDateFilter,
	type InvoiceMonthlyDetailDateFilterValue,
} from './InvoiceMonthlyDetailDateFilter';
import { SubscriberTypeFilter } from './SubscriberTypeFilter';
import type { ReportFormProps } from './types';

const initialDateFilter: InvoiceMonthlyDetailDateFilterValue = {
	invoiceStartDate: null,
	invoiceEndDate: null,
	parkingPeriodStartMonth: '',
	parkingPeriodStartYear: '',
	parkingPeriodEndMonth: '',
	parkingPeriodEndYear: '',
};

const initialFacilityHierarchy: FacilityHierarchyFilterValue = {
	areas: [],
	branches: [],
	districts: [],
	facilities: [],
};

const hasParkingPeriodStart = (dateFilter: InvoiceMonthlyDetailDateFilterValue) => {
	return dateFilter.parkingPeriodStartMonth !== '' && dateFilter.parkingPeriodStartYear !== '';
};

export const InvoiceMonthlyDetailReportForm = ({
	createReport,
	isGeneratingReport,
	organizationId,
	onValidationError,
}: ReportFormProps) => {
	const [dateFilter, setDateFilter] = useState<InvoiceMonthlyDetailDateFilterValue>(initialDateFilter);
	const [accountFilter, setAccountFilter] = useState<AccountFilterValue>({ type: 'all' });
	const [feeTypes, setFeeTypes] = useState<InvoiceMonthlyDetailFeeType[]>([...invoiceMonthlyDetailDefaultFeeTypes]);
	const [subscriberType, setSubscriberType] = useState<SubscriberType>('All');
	const [facilityHierarchy, setFacilityHierarchy] = useState<FacilityHierarchyFilterValue>(initialFacilityHierarchy);

	const canSubmit = useMemo(() => {
		if (feeTypes.length === 0) {
			return false;
		}

		return Boolean(dateFilter.invoiceStartDate) || hasParkingPeriodStart(dateFilter);
	}, [dateFilter, feeTypes]);

	const reset = useCallback(() => {
		setDateFilter(initialDateFilter);
		setAccountFilter({ type: 'all' });
		setFeeTypes([...invoiceMonthlyDetailDefaultFeeTypes]);
		setSubscriberType('All');
		setFacilityHierarchy(initialFacilityHierarchy);
	}, []);

	const buildAccountFilter = (): InvoiceMonthlyDetailParameters['accountFilter'] => {
		switch (accountFilter.type) {
			case 'accountId':
				return {
					type: 'accountId',
					value: Number(accountFilter.value),
				};

			case 'subscriptionName':
				return {
					type: 'subscriptionName',
					value: accountFilter.value.trim(),
				};
			default:
				return undefined;
		}
	};

	const buildParameters = (): InvoiceMonthlyDetailParameters => {
		const parameters: InvoiceMonthlyDetailParameters = {
			feeTypes,
			subscriberType,
		};

		if (dateFilter.invoiceStartDate) {
			parameters.invoiceStartDate = dateFilter.invoiceStartDate.format('YYYY-MM-DD');

			if (dateFilter.invoiceEndDate) {
				parameters.invoiceEndDate = dateFilter.invoiceEndDate.format('YYYY-MM-DD');
			}
		}

		if (hasParkingPeriodStart(dateFilter)) {
			parameters.parkingPeriodStartDate = dayjs()
				.year(dateFilter.parkingPeriodStartYear as number)
				.month(dateFilter.parkingPeriodStartMonth as number)
				.startOf('month')
				.format('YYYY-MM-DD');

			if (dateFilter.parkingPeriodEndMonth !== '' && dateFilter.parkingPeriodEndYear !== '') {
				parameters.parkingPeriodEndDate = dayjs()
					.year(dateFilter.parkingPeriodEndYear)
					.month(dateFilter.parkingPeriodEndMonth)
					.endOf('month')
					.format('YYYY-MM-DD');
			}
		}

		const accountFilterValue = buildAccountFilter();

		if (accountFilterValue) {
			parameters.accountFilter = accountFilterValue;
		}

		applyFacilityHierarchyToParameters(parameters, facilityHierarchy);

		return parameters;
	};

	const validate = () => {
		if (feeTypes.length === 0) {
			onValidationError('Select at least one fee type.');
			return false;
		}

		if (!dateFilter.invoiceStartDate && !hasParkingPeriodStart(dateFilter)) {
			onValidationError('Select an invoice start date or a parking period start month and year.');
			return false;
		}

		if (
			(dateFilter.parkingPeriodEndMonth !== '' || dateFilter.parkingPeriodEndYear !== '') &&
			!hasParkingPeriodStart(dateFilter)
		) {
			onValidationError('Select a parking period start before specifying a parking period end.');
			return false;
		}

		if (accountFilter.type === 'accountId' && !accountFilter.value) {
			onValidationError('Enter an account ID.');
			return false;
		}

		if (accountFilter.type === 'subscriptionName' && !accountFilter.value.trim()) {
			onValidationError('Enter a subscription name.');
			return false;
		}

		return true;
	};

	const submit = async () => {
		if (!validate()) {
			return;
		}

		await createReport({
			reportType: 'invoiceMonthlyDetail',
			parameters: buildParameters(),
		});
	};

	return (
		<Card variant='outlined' sx={{ p: 3 }}>
			<Stack spacing={3}>
				<InvoiceMonthlyDetailDateFilter value={dateFilter} onChange={setDateFilter} />

				<Divider />

				<AccountFilter filter={accountFilter} onChange={setAccountFilter} />

				<Divider />

				<SubscriberTypeFilter value={subscriberType} onChange={setSubscriberType} />

				<Divider />

				<FeeTypesFilter selectedFeeTypes={feeTypes} onChange={setFeeTypes} />

				<Divider />

				<FacilityHierarchyFilter
					organizationId={organizationId}
					value={facilityHierarchy}
					onChange={setFacilityHierarchy}
				/>

				<Divider />

				<Stack direction='row' justifyContent='flex-end' spacing={2}>
					<Button onClick={reset} disabled={isGeneratingReport}>
						Reset
					</Button>

					<Button onClick={submit} disabled={!canSubmit || isGeneratingReport}>
						Generate Report
					</Button>
				</Stack>
			</Stack>
		</Card>
	);
};
